import React, { Suspense } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import AccessDenied from './components/AccessDenied';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { Skeleton } from './components/ui/skeleton';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import MyApps from './pages/MyApps';
import MyProcessorTemplates from './pages/MyProcessorTemplates';
import MyLenses from './pages/MyLenses';
import Roles from './pages/Roles';
import Tags from './pages/Tags';
import MyOrg from './pages/MyOrg';
import AccountSettings from './pages/AccountSettings';
import Callback from './pages/Callback';
import NotFound from './pages/NotFound';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { UserProvider } from './contexts/UserContext';
import { usePermissions } from './hooks/usePermissions';
import { useOrganization } from './contexts/OrganizationContext';
import { ROUTES, SYSTEM_ADMIN_PERMISSIONS, SYSTEM_ADMIN_TABS } from './routes';

// Route-level code splitting for the heavyweight pages: Sensors carries
// Monaco + Leaflet, the builder carries Leaflet, and SystemAdmin is a panel
// per SYSTEM_ADMIN_TABS entry — keeping them out of the main chunk keeps first
// load light.
const Sensors = React.lazy(() => import('./pages/Sensors'));
const AppRequestBuilder = React.lazy(() => import('./pages/AppRequestBuilder'));
const SystemAdmin = React.lazy(() => import('./pages/SystemAdmin'));

// Kitchen sink for the design system — dev-only by design decision: the
// constant-folded condition removes both the route and its chunk from
// production builds.
const DesignSystem = import.meta.env.DEV
  ? React.lazy(() => import('./pages/DesignSystem'))
  : null;

interface PermissionGateProps {
  /** Single permission string, or array for OR-logic (any one grants access). */
  permission: string | string[];
  children: React.ReactNode;
}

/**
 * Renders children only if the active org grants the required permission(s).
 * While permissions resolve it holds the layout with skeletons (no white
 * flash); denials render an explanatory panel in place — never a redirect.
 */
const PageSkeleton: React.FC = () => (
  <div aria-busy="true" className="space-y-4">
    <Skeleton className="h-8 w-56" />
    <Skeleton className="h-4 w-80" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const PermissionGate: React.FC<PermissionGateProps> = ({ permission, children }) => {
  const { isLoading, hasAnyPermission } = usePermissions();
  const { membershipError } = useOrganization();
  if (isLoading) {
    return <PageSkeleton />;
  }
  // The shell's "Couldn't load your organizations" banner is the one true
  // explanation. With no memberships there are no permissions either, and an
  // access denial under that banner would blame the user's role.
  if (membershipError) return null;
  const required = Array.isArray(permission) ? permission : [permission];
  // An empty list means the route is open to any authenticated member.
  const granted = required.length === 0 || hasAnyPermission(required);
  if (!granted) return <AccessDenied />;
  return (
    // Lazy route chunks load under the same skeleton the gate uses.
    <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
  );
};

/** Permission lookup from the route registry, so gates can't drift from nav. */
const routePermission = (path: string): string[] => {
  const meta = ROUTES.find((route) => route.path === path);
  return meta?.permissions ?? [];
};

function App() {
  return (
    <Router>
      {/* One shared tooltip provider: moving between adjacent row tooltips
          skips the delay. Tooltip carries its own when none is mounted. */}
      <TooltipProvider>
      <Toaster />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        {/* Where an invitation's "Set your password" link lands. The legacy
            auth paths below keep pointing at /login on purpose (pinned by
            e2e/specs/public-surfaces.spec.ts): the hosted sign-in page offers
            its own "Forgot your password?", so /login is not a dead end. */}
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
        <Route path="/callback" element={<Callback />} />
        <Route path="/legal/terms-of-service" element={<TermsOfService />} />
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <UserProvider>
                <OrganizationProvider>
                  <Layout />
                </OrganizationProvider>
              </UserProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/my-apps" replace />} />
          <Route path="my-apps" element={<PermissionGate permission={routePermission('/my-apps')}><MyApps /></PermissionGate>} />
          <Route path="my-processor-templates" element={<PermissionGate permission={routePermission('/my-processor-templates')}><MyProcessorTemplates /></PermissionGate>} />
          <Route path="lenses" element={<PermissionGate permission={routePermission('/lenses')}><MyLenses /></PermissionGate>} />
          <Route path="sensors" element={<PermissionGate permission={routePermission('/sensors')}><Sensors /></PermissionGate>} />
          <Route path="tags" element={<PermissionGate permission={routePermission('/tags')}><Tags /></PermissionGate>} />
          <Route path="my-org" element={<PermissionGate permission={routePermission('/my-org')}><MyOrg /></PermissionGate>} />
          <Route path="my-org/roles" element={<PermissionGate permission={routePermission('/my-org/roles')}><Roles /></PermissionGate>} />
          <Route path="app-request-builder" element={<PermissionGate permission={routePermission('/app-request-builder')}><AppRequestBuilder /></PermissionGate>} />
          <Route path="account-settings" element={<AccountSettings />} />
          {/*
            The bare /system-admin path keeps the OR-union: it is only a
            redirect to the first sub-page the caller may open, so anyone with
            ANY system read permission has to be able to reach it.
          */}
          <Route
            path="system-admin"
            element={<PermissionGate permission={SYSTEM_ADMIN_PERMISSIONS}><SystemAdmin /></PermissionGate>}
          />
          {/*
            Each sub-page gates on its OWN permission. Previously all six shared
            the union, so a caller holding one system permission could open the
            URL of a surface they could not see and the page had to detect that
            and render AccessDenied inside its own shell. This is the same gate
            every other route uses.
          */}
          {SYSTEM_ADMIN_TABS.map((tab) => (
            <Route
              key={tab.value}
              path={`system-admin/${tab.value}`}
              element={<PermissionGate permission={tab.permission}><SystemAdmin /></PermissionGate>}
            />
          ))}
          {/* import.meta.env.DEV is replaced with a literal by Vite, so Rollup
              folds this whole branch away — the route path string, the lazy
              import and the DesignSystem chunk are absent from production.
              e2e/specs/design-system-build-guard.spec.ts proves it. */}
          {import.meta.env.DEV && DesignSystem ? (
            <Route
              path="design-system"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <DesignSystem />
                </Suspense>
              }
            />
          ) : null}
          {/* Legacy redirect */}
          <Route path="geo-area-builder" element={<Navigate to="/app-request-builder" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </TooltipProvider>
    </Router>
  );
}

export default App;
